import React, { useEffect } from 'react';
import { Trans, useTranslation } from '@wix/yoshi-flow-editor';
import {
  SectionNotification,
  SectionNotificationType,
  Spinner,
  SpinnerTypes,
  TextButton,
} from 'wix-ui-tpa/cssVars';
import { useFormActions } from '../../Hooks/useFormActions';
import { useSettingsAdapter } from '../../Settings/Utils/SettingsAdapter';
import settingsParams from '../../settingsParams';
import { classes, st } from './LoginBar.st.css';
import { LoginBarDataHooks } from './dataHooks';

export interface LoginBarProps {
  isLoggedIn: boolean;
  isLoggingOut: boolean;
  email?: string;
  onLogin?: () => void;
}

const LoginBar: React.FC<LoginBarProps> = ({
  isLoggedIn,
  isLoggingOut,
  email,
  onLogin,
}) => {
  const { t } = useTranslation();
  const { promptLogin, logout } = useFormActions();
  const theme = useSettingsAdapter().get(settingsParams.fieldsBorderStyle);

  useEffect(() => {
    if (!isLoggingOut) {
      return;
    }
    const reloadIfRestored = (event: PageTransitionEvent) => {
      if (event.persisted) {
        window.location.reload();
      }
    };
    window.addEventListener('pageshow', reloadIfRestored);
    return () => window.removeEventListener('pageshow', reloadIfRestored);
  }, [isLoggingOut]);

  return (
    <div className={st(classes.root, { theme })}>
      <SectionNotification
        data-hook={LoginBarDataHooks.ROOT}
        type={SectionNotificationType.wired}
        size="regular"
      >
        <SectionNotification.Text data-hook={LoginBarDataHooks.TEXT}>
          {isLoggedIn ? (
            email && t('app.bookings-form.login-bar.logged-in', { email })
          ) : (
            <Trans
              i18nKey="app.bookings-form.login-bar.have-account-login"
              components={[
                <TextButton
                  key="login"
                  data-hook={LoginBarDataHooks.LOGIN_BUTTON}
                  className={classes.button}
                  onClick={
                    onLogin ?? (() => promptLogin({ dialog: 'site-default' }))
                  }
                />,
              ]}
            />
          )}
        </SectionNotification.Text>
        {isLoggedIn &&
          (isLoggingOut ? (
            <Spinner
              data-hook={LoginBarDataHooks.SPINNER}
              type={SpinnerTypes.slim}
              diameter={20}
            />
          ) : (
            <TextButton
              data-hook={LoginBarDataHooks.LOGOUT_BUTTON}
              className={classes.button}
              onClick={() => logout()}
            >
              {t('app.bookings-form.login-bar.logout')}
            </TextButton>
          ))}
      </SectionNotification>
    </div>
  );
};

export default LoginBar;
